import React, { ReactNode } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../context/ThemeContext';
import { cardEdgeStyle } from '../../theme/cardEdge';
import AuthBrandHeader from '../auth/AuthBrandHeader';
import { isWeb } from '../../utils/platformUtils';
import {
  WEB_AUTH_CARD_MAX_WIDTH,
  WEB_AUTH_CARD_MIN_HEIGHT,
  WEB_AUTH_BRAND_PANEL_WIDTH,
  WEB_AUTH_BRAND_MAX_WIDTH_PERCENT,
} from '../../theme/webLayout';

const DEFAULT_FEATURES = [
  'Track savings, debt, and investment goals',
  'Allocate income across priorities',
  'See progress at a glance',
];

type Props = {
  cardTitle: string;
  cardSubtitle?: string;
  children: ReactNode;
  onBack?: () => void;
  features?: string[];
};

const WebAuthLayout = ({ cardTitle, cardSubtitle, children, onBack, features = DEFAULT_FEATURES }: Props) => {
  const { colors } = useTheme();

  if (!isWeb) {
    return <>{children}</>;
  }

  return (
    <View style={styles.root}>
      <View style={styles.brandPanel}>
        <LinearGradient
          colors={[colors.auth.panel, colors.primary.dark]}
          style={StyleSheet.absoluteFill}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        />
        <View style={styles.brandInner}>
          <AuthBrandHeader />
          <Text style={styles.tagline}>Smart Financial Goals</Text>
          <View style={styles.features}>
            {features.map(line => (
              <Text key={line} style={styles.featureLine}>
                • {line}
              </Text>
            ))}
          </View>
        </View>
      </View>

      <View style={[styles.formPanel, { backgroundColor: colors.background.paper }]}>
        <ScrollView
          contentContainerStyle={styles.formPanelScroll}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View
            style={[
              styles.card,
              { backgroundColor: colors.surface },
              cardEdgeStyle(colors),
              colors.cardEdge === 'transparent' && { borderColor: colors.border },
            ]}
          >
            {onBack ? (
              <TouchableOpacity onPress={onBack} style={styles.backRow} accessibilityRole="button">
                <Ionicons name="arrow-back" size={22} color={colors.brand.accent} />
                <Text style={[styles.backText, { color: colors.brand.accent }]}>Back</Text>
              </TouchableOpacity>
            ) : null}
            <Text style={[styles.cardTitle, { color: colors.text.primary }]}>{cardTitle}</Text>
            {cardSubtitle ? (
              <Text style={[styles.cardSubtitle, { color: colors.text.secondary }]}>{cardSubtitle}</Text>
            ) : null}
            {children}
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100vh' as unknown as number,
    width: '100%',
  },
  brandPanel: {
    width: WEB_AUTH_BRAND_PANEL_WIDTH,
    maxWidth: `${WEB_AUTH_BRAND_MAX_WIDTH_PERCENT}%` as unknown as number,
    minWidth: 280,
    flexGrow: 0,
    flexShrink: 0,
    overflow: 'hidden',
  },
  brandInner: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 48,
  },
  tagline: {
    fontSize: 17,
    color: 'rgba(255,255,255,0.9)',
    marginTop: 6,
    marginBottom: 28,
    textAlign: 'center',
  },
  features: {
    gap: 10,
    maxWidth: 380,
    alignItems: 'center',
  },
  featureLine: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.92)',
    lineHeight: 22,
    textAlign: 'center',
  },
  formPanel: {
    flex: 1,
    minWidth: 0,
  },
  formPanelScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 48,
    paddingHorizontal: 56,
  },
  card: {
    width: '100%',
    maxWidth: WEB_AUTH_CARD_MAX_WIDTH,
    alignSelf: 'center',
    borderRadius: 16,
    padding: 40,
    borderWidth: 1,
    ...(Platform.OS === 'web'
      ? {
          minHeight: WEB_AUTH_CARD_MIN_HEIGHT,
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        }
      : {}),
  },
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 16,
  },
  backText: {
    fontSize: 15,
    fontWeight: '600',
  },
  cardTitle: {
    fontSize: 26,
    fontWeight: '700',
    marginBottom: 6,
  },
  cardSubtitle: {
    fontSize: 15,
    marginBottom: 24,
    lineHeight: 22,
  },
});

export default WebAuthLayout;
